import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { IconTile } from "./IconTile";
import { SectionLabel } from "./SectionLabel";
import type { IProfileQuickLink } from "../types";

interface SettingsLinksProps {
  links: IProfileQuickLink[];
  onNavigate: (path: string) => void;
}

/**
 * Personal-settings shortcuts. Rows are real `Button`s (ghost variant) rather
 * than hand-rolled `<button className="bg-…">`, so they inherit the design
 * system's hover, disabled and focus-ring behaviour.
 *
 * Each row answers hover on three channels — a brand rail grows in from the
 * left edge, the icon tile tints, and the chevron slides — so intent is legible
 * without the row itself changing size.
 */
export const SettingsLinks = ({ links, onNavigate }: SettingsLinksProps) => {
  const { t } = useTranslation(["common"]);
  return (
    <nav aria-label={t("common:a11y.account_settings")} className="space-y-2">
      <SectionLabel>{t("nav:modules.settings")}</SectionLabel>
      <div className="divide-y divide-border overflow-hidden rounded-sm border border-border bg-surface">
        {links.map(({ icon: Icon, label, path, badge }) => (
          <Button
            key={label}
            variant="ghost"
            onClick={() => onNavigate(path)}
            className="group h-12 w-full justify-start gap-3 rounded-none px-3 before:absolute before:inset-y-0 before:start-0 before:w-0 before:bg-primary before:transition-[width] before:duration-200 hover:bg-primary-muted/40 hover:before:w-[3px] focus-visible:ring-inset focus-visible:ring-offset-0"
          >
            <IconTile interactive>
              <Icon size={15} />
            </IconTile>
            <span className="flex-1 truncate text-start text-sm font-medium">
              {label}
            </span>
            {badge && (
              <Badge
                variant={badge.variant}
                className="shrink-0 px-1.5 py-0 text-[10px]"
              >
                {badge.text}
              </Badge>
            )}
            <ChevronRight
              size={15}
              aria-hidden
              className="shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary rtl-flip"
            />
          </Button>
        ))}
      </div>
    </nav>
  );
};
